'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';

import { Icons } from 'app/constants/icons';
import { useAuthentication } from 'app/hooks/useAuthentication';
import { useIsMobile } from 'app/hooks/useIsMobile';
import { cn } from 'app/utils/styles.utils';

import { useMobileMenu } from './MobileMenu';
import { Button } from 'app/shared/buttons/Button';
import { Link } from 'app/shared/buttons/Link';
import { AfterEffectAuth } from 'app/shared/misc/AfterRenderAuth';

type AuthButtonProps = {
  iconSize?: number;
  isOnlyIcon?: boolean;
};

export function AuthButton(props: AuthButtonProps) {
  return (
    <AfterEffectAuth>
      <Render {...props} />
    </AfterEffectAuth>
  );
}

const Render = ({iconSize = 18, isOnlyIcon}: AuthButtonProps) => {
  const setOpen = useMobileMenu(state => state.setOpen);
  const router = useRouter();

  const {connected, disconnect} = useAuthentication();
  const isMobile = useIsMobile();

  const handleDisconnect = useCallback(() => {
    disconnect();
    setOpen(false);
    router.push('/');
  }, [disconnect, setOpen, router]);

  if (connected) {
    return (
      <Button
        className={cn(
          'flex h-full p-2 transition-all hover:opacity-90 active:opacity-80',
          {
            'gap-2.5 !rounded-lg': isMobile,
          },
        )}
        size={isMobile && !isOnlyIcon ? 'md' : undefined}
        variant={
          isMobile && !isOnlyIcon ? 'secondary-gray-contained' : undefined
        }
        onClick={handleDisconnect}
        aria-label="Disconnect">
        <span className="w-5">
          <Icons.logout size={iconSize} strokeWidth={2.5} />
        </span>

        {isMobile && !isOnlyIcon && <span>Logout</span>}
      </Button>
    );
  }

  return (
    <Link
      variant="primary-contained"
      className="items-center justify-center gap-2.5 pr-3"
      size={isMobile ? 'sm' : 'md'}
      href="/connect">
      Sign In
      <Icons.chevronRight width="18" strokeWidth={2.8} />
    </Link>
  );
};
